import { useEffect, useMemo, useState, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus, Search, Eye, Edit, ShieldCheck, Phone, Mail, MapPin, MoreHorizontal, Power, Users,
} from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import { ContractorDialog } from '../components/dialogs/ContractorDialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const KIND_META = {
  company:  { label: 'Company',  cls: 'bg-[#0D5B69]/10 text-[#0D5B69] border-[#0D5B69]/20' },
  employee: { label: 'Employee', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  vehicle:  { label: 'Vehicle',  cls: 'bg-[#80C030]/15 text-[#5a8c20] border-[#80C030]/30' },
};

export const Contractors = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  const [contractors, setContractors] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [kindFilter, setKindFilter] = useState('all'); // all | company | employee | vehicle

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get('/contractors', { params: { include_inactive: showInactive } }),
        api.get('/services'),
      ]);
      setContractors(cRes.data || []);
      setServices(sRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load contractors');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const serviceById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return contractors.filter((c) => {
      if (kindFilter !== 'all' && (c.kind || 'company') !== kindFilter) return false;
      if (!q) return true;
      return (
        (c.company_name || '').toLowerCase().includes(q) ||
        (c.code || '').toLowerCase().includes(q) ||
        (c.contact_person || '').toLowerCase().includes(q) ||
        (c.area_of_operation || '').toLowerCase().includes(q) ||
        (c.registration_number || '').toLowerCase().includes(q)
      );
    });
  }, [contractors, searchTerm, kindFilter]);

  // Per-kind counts shown on the filter chips
  const kindCounts = useMemo(() => {
    const counts = { all: contractors.length, company: 0, employee: 0, vehicle: 0 };
    contractors.forEach((c) => {
      const k = c.kind || 'company';
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [contractors]);

  const totals = {
    total: contractors.length,
    active: contractors.filter((c) => c.active).length,
    personnel: contractors.reduce((acc, c) => acc + (c.personnel?.length || 0), 0),
  };

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setDialogOpen(true); };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/contractors/${c.id}`, { active: !c.active });
      toast.success(c.active ? `${c.company_name} deactivated` : `${c.company_name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Contractors" subtitle="Operational Resource Management">
      <div className="space-y-6 animate-fade-in" data-testid="contractors-page">

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Total Contractors" value={totals.total} icon={ShieldCheck} accent="#0D5B69" />
          <StatCard label="Active" value={totals.active} accent="#80C030" />
          <StatCard label="Total Personnel" value={totals.personnel} icon={Users} accent="#0D5B69" />
        </div>

        <Card className="bg-white border-slate-200" data-testid="contractors-table-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4 space-y-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Contractor Directory</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text" placeholder="Search contractors..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-56" data-testid="contractor-search"
                  />
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setShowInactive((v) => !v)}
                  data-testid="toggle-inactive"
                >
                  {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                </Button>
                {canEdit && (
                  <Button
                    onClick={openCreate}
                    className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
                    data-testid="add-contractor-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Contractor
                  </Button>
                )}
              </div>
            </div>

            {/* Show-only kind filter chips */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="contractor-kind-filter">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">Show only:</span>
              {[
                { value: 'all',      label: 'All' },
                { value: 'company',  label: 'Company' },
                { value: 'employee', label: 'Employee' },
                { value: 'vehicle',  label: 'Vehicle' },
              ].map((opt) => {
                const active = kindFilter === opt.value;
                const meta = KIND_META[opt.value];
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setKindFilter(opt.value)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                      active
                        ? (meta ? `${meta.cls} ring-2 ring-current/30` : 'bg-[#0D5B69] text-white border-[#0D5B69] ring-2 ring-[#0D5B69]/30')
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`kind-chip-${opt.value}`}
                    aria-pressed={active}
                  >
                    {opt.label}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/30' : 'bg-slate-100 text-slate-500'}`}>
                      {kindCounts[opt.value] || 0}
                    </span>
                  </button>
                );
              })}
              {kindFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setKindFilter('all')}
                  className="ml-2 text-[10px] text-slate-400 underline hover:text-slate-600"
                  data-testid="kind-chip-clear"
                >
                  clear filter
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contractor</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Services</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Area</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Personnel</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">Loading contractors…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-400" data-testid="empty-contractors">
                      {searchTerm ? 'No contractors match your search.' : 'No contractors yet. Add your first contractor to get started.'}
                    </TableCell>
                  </TableRow>
                ) : filtered.map((c, idx) => (
                  <TableRow key={c.id} className="hover:bg-slate-50/80" data-testid={`contractor-row-${idx}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-slate-600">
                            {(c.company_name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-slate-900 truncate">{c.company_name}</p>
                            <Badge className={`text-[10px] font-semibold border ${KIND_META[c.kind || 'company'].cls} hover:opacity-100`} data-testid={`contractor-kind-badge-${idx}`}>
                              {KIND_META[c.kind || 'company'].label}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">
                            {c.code || '—'}
                            {c.kind === 'vehicle' && c.registration_number && (
                              <span className="ml-2 font-mono font-bold text-[#5a8c20]">· {c.registration_number}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.contact_person && <p className="text-sm text-slate-900">{c.contact_person}</p>}
                      {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {c.phone}</p>}
                      {c.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</p>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(c.services_provided || []).slice(0, 3).map((sid) => (
                          <Badge key={sid} variant="outline" className="text-[10px] bg-slate-50">
                            {serviceById[sid]?.name?.split(' ')[0] || sid}
                          </Badge>
                        ))}
                        {(c.services_provided || []).length > 3 && (
                          <span className="text-xs text-slate-400 self-center">+{c.services_provided.length - 3}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {c.area_of_operation && (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {c.area_of_operation}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {(c.personnel || []).length}
                    </TableCell>
                    <TableCell>
                      {c.active ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`contractor-actions-${idx}`}>
                            <MoreHorizontal className="w-4 h-4 text-slate-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="gap-2" onClick={() => openEdit(c)}>
                            <Eye className="w-4 h-4" /> View / Edit
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem className="gap-2" onClick={() => openEdit(c)}>
                              <Edit className="w-4 h-4" /> Edit Details
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem className="gap-2" onClick={() => toggleActive(c)} data-testid={`contractor-toggle-${idx}`}>
                              <Power className="w-4 h-4" /> {c.active ? 'Deactivate' : 'Reactivate'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <ContractorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contractor={editing}
        services={services}
        onSaved={load}
      />
    </Layout>
  );
};

const StatCard = ({ label, value, icon: Icon, accent = '#0D5B69' }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold font-heading" style={{ color: accent }}>{value}</p>
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);
